import React from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { Card, Col, Image, Row, Space, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { queries } from '../../../graphql/graphql';
import { Loader } from '../../../components/UI/Loader';
import type { Proposal, ProposalFormField } from '../../../types/proposal';

const { Title, Text, Paragraph } = Typography;

interface ContractorProposalViewProps {
  id?: string;
}

export const ContractorProposalView: React.FC<ContractorProposalViewProps> = ({ id }) => {
  const params = useParams();
  const proposalId = id || params.id;

  const { data, loading } = useQuery(queries.proposal.PROPOSAL, {
    variables: { id: proposalId },
    skip: !proposalId,
    fetchPolicy: 'network-only',
  });

  const proposal: Proposal | undefined = data?.proposal;

  if (loading) {
    return <Loader />;
  }

  if (!proposal) {
    return <Paragraph>Proposal not found.</Paragraph>;
  }

  const address = proposal.mapResult?.map?.address || 'Address pending';
  const snapshot = proposal.mapResult?.snapshot || null;
  const plan = proposal.plan ?? null;
  const planTitle = plan?.title || 'Plan';
  const planSvgUrl =
    plan?.planSVG && plan?.planId ? `https://3d.homewip.com/svg/${plan.planId}/` : null;
  const planPhotos = Array.isArray(plan?.photos) ? plan.photos : [];
  const primaryPhoto = planPhotos.find(photo => photo?.isPrimary) ?? planPhotos[0] ?? null;
  const primaryPhotoUrl = primaryPhoto?.original || primaryPhoto?.compressed?.url || null;
  const currency = proposal.formPayload?.currency || 'USD';
  const contractorNotes = proposal.formPayload?.contractorNotes?.trim();
  const planMeta = plan?.meta || {};
  const bedrooms = typeof planMeta?.bedroomCount === 'number' ? planMeta.bedroomCount : null;
  const bathrooms = typeof planMeta?.bathroomCount === 'number' ? planMeta.bathroomCount : null;
  const totalArea =
    typeof planMeta?.outerArea === 'number'
      ? planMeta.outerArea
      : typeof planMeta?.totalArea === 'number'
        ? planMeta.totalArea
        : null;

  const metaSummaryParts = [
    bedrooms ? `${bedrooms} BD` : null,
    bathrooms ? `${bathrooms} BATH` : null,
  ].filter(Boolean);
  const metaSummary = metaSummaryParts.join(' / ') + (totalArea ? ` ${totalArea} SQFT` : '');
  const heroTitle = metaSummary || planTitle;
  const contractorName = proposal.contractor?.name || 'Contractor';
  const estimateTotal =
    typeof proposal.estimate?.total === 'number' ? proposal.estimate.total : null;
  const estimateCost =
    estimateTotal !== null
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(estimateTotal)
      : '—';
  const timelineUnitLabel = proposal.timeline?.unit === 'MONTHS' ? 'months' : 'weeks';
  const timelineFrom = typeof proposal.timeline?.from === 'number' ? proposal.timeline.from : null;
  const timelineTo = typeof proposal.timeline?.to === 'number' ? proposal.timeline.to : null;
  const hasTimelineFrom = typeof timelineFrom === 'number';
  const hasTimelineTo = typeof timelineTo === 'number';
  const estimateTimeline =
    hasTimelineFrom && hasTimelineTo
      ? `${timelineFrom}–${timelineTo} ${timelineUnitLabel}`
      : hasTimelineFrom
        ? `${timelineFrom} ${timelineUnitLabel}`
        : hasTimelineTo
          ? `${timelineTo} ${timelineUnitLabel}`
          : '—';

  const formatUnit = (unit?: string) => {
    if (!unit) return '';
    return unit.replace(/_/g, ' ');
  };

  const formatQuantityLabel = (quantity?: number, unit?: string) => {
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity === 1) {
      return null;
    }

    const formattedQuantity = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(quantity);

    if (!unit) {
      return formattedQuantity;
    }

    const measurementUnits = new Set(['sq_ft', 'linear_ft']);
    const baseUnit = formatUnit(unit);
    if (measurementUnits.has(unit)) {
      return `${formattedQuantity} ${baseUnit}`;
    }

    const needsEs =
      baseUnit.endsWith('ch') ||
      baseUnit.endsWith('sh') ||
      baseUnit.endsWith('s') ||
      baseUnit.endsWith('x') ||
      baseUnit.endsWith('z');
    const pluralUnit = needsEs
      ? `${baseUnit}es`
      : baseUnit.endsWith('y') && !/[aeiou]y$/.test(baseUnit)
        ? `${baseUnit.slice(0, -1)}ies`
        : `${baseUnit}s`;

    return `${formattedQuantity} ${pluralUnit}`;
  };

  const formatPrice = (value?: number | null) => {
    if (typeof value !== 'number') {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getCategorySubtotal = (category: { subtotal?: number | null; fields: ProposalFormField[] }) => {
    if (typeof category.subtotal === 'number' && Number.isFinite(category.subtotal)) {
      return category.subtotal;
    }
    const fields = Array.isArray(category.fields) ? category.fields : [];
    return fields.reduce((total, field) => {
      const value = Number(field.value);
      return Number.isFinite(value) ? total + value : total;
    }, 0);
  };

  const renderFieldRow = (field: ProposalFormField, index: number) => {
    const quantityLabel = formatQuantityLabel(field.quantity, field.unit);
    const hasValue = typeof field.value === 'number' && Number.isFinite(field.value);
    const valueIcon = hasValue ? (
      <CheckOutlined style={{ color: '#1f1f1f', fontSize: 18 }} />
    ) : (
      <CloseOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
    );

    return (
      <div
        key={field.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0',
        }}>
        <Text type="secondary" style={{ width: 32 }}>
          {String(index + 1).padStart(2, '0')}
        </Text>
        <div style={{ flex: 1 }}>
          <Text>{field.label}</Text> <Text type="secondary">{quantityLabel}</Text>
          {field.note && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {field.note}
            </Text>
          )}
        </div>
        <div style={{ textAlign: 'right', minWidth: 120, display: 'flex', justifyContent: 'flex-end' }}>
          {valueIcon}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%', textAlign: 'left', padding: '24px' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <Title
            level={1}
            style={{
              marginBottom: 8,
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.04em',
              fontSize: 'clamp(28px, 6vw, 56px)',
            }}>
            {heroTitle}
          </Title>
        </div>

        {primaryPhotoUrl && (
          <Image
            src={primaryPhotoUrl}
            alt={`${planTitle} primary`}
            width="100%"
            style={{ maxHeight: '100vh', borderRadius: 20, objectFit: 'cover' }}
            preview={false}
          />
        )}

        {(snapshot || planSvgUrl) && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Title
                level={4}
                style={{
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontSize: '42px',
                }}>
                Your Project. Defined. Delivered.
              </Title>
            </Col>
            <Col span={24}>
              <Text
                style={{
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontSize: '32px',
                }}>
                {address}
              </Text>
            </Col>
            {snapshot && (
              <Col xs={24} md={12}>
                <Image
                  src={snapshot}
                  alt="Map preview"
                  width="100%"
                  style={{ maxHeight: 450, borderRadius: 16, objectFit: 'cover' }}
                  preview
                />
              </Col>
            )}
            {planSvgUrl && (
              <Col xs={24} md={12}>
                <Image
                  src={planSvgUrl}
                  alt="Floor plan"
                  width="100%"
                  style={{ maxHeight: 450, borderRadius: 16, objectFit: 'contain' }}
                  preview
                />
              </Col>
            )}
          </Row>
        )}

        {proposal.formPayload ? (
          proposal.formPayload.categories.map((category, categoryIndex) => {
            const categorySubtotal = getCategorySubtotal(category);
            const imageFirst = categoryIndex % 2 === 1;

            const contentColumn = (
              <Col xs={24} md={category.image ? 12 : 24}>
                <Title
                  level={4}
                  style={{
                    marginTop: 0,
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    fontSize: '38px',
                  }}>
                  {category.title}
                </Title>
                <div>{category.fields.map((field, index) => renderFieldRow(field, index))}</div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '12px 0',
                  }}>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary">Subtotal</Text>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: 120 }}>
                    <Text>{formatPrice(categorySubtotal)}</Text>
                  </div>
                </div>
              </Col>
            );
            const imageColumn = category.image && (
              <Col xs={24} md={12}>
                <Image
                  src={category.image}
                  alt={category.id}
                  width="100%"
                  style={{
                    maxHeight: category.fields.length * 55 || 600,
                    borderRadius: 16,
                    objectFit: 'cover',
                  }}
                  preview={false}
                />
              </Col>
            );

            return (
              <Card key={category.id} style={{ borderRadius: 16 }}>
                <Row gutter={[24, 16]} align="top">
                  {imageFirst ? imageColumn : contentColumn}
                  {imageFirst ? contentColumn : imageColumn}
                </Row>
              </Card>
            );
          })
        ) : (
          <Paragraph>No proposal form data yet.</Paragraph>
        )}

        {contractorNotes && (
          <Card style={{ borderRadius: 16 }}>
            <Title
              level={4}
              style={{
                marginTop: 0,
                fontWeight: 500,
                letterSpacing: '0.04em',
                fontSize: '32px',
              }}>
              Exclusions
            </Title>
            <Paragraph style={{ fontSize: 18, marginBottom: 0 }}>{contractorNotes}</Paragraph>
          </Card>
        )}

        <Card style={{ borderRadius: 16 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Text
                style={{
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontSize: '32px',
                  display: 'block',
                  marginBottom: 8,
                }}>
                {contractorName.toUpperCase()}
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Contractor
              </Text>
              <Text type="secondary">Licensed, insured, and pre-approved for your region.</Text>
            </Col>
            <Col xs={24} md={8}>
              <Text
                style={{
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontSize: '32px',
                  display: 'block',
                  marginBottom: 8,
                }}>
                {estimateCost}
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Estimated Cost
              </Text>
              <Text type="secondary">
                Comprehensive scope including materials, labor.
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Text
                style={{
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  fontSize: '32px',
                  display: 'block',
                  marginBottom: 8,
                }}>
                {estimateTimeline}
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Estimated Timeline
              </Text>
              <Text type="secondary">
                Aligned with standard build cycles and inspection milestones.
              </Text>
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};
